import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, WindowContext } from '../api';
import { dollars, endpoints, when } from '../api';
import { useManagerPin, usePermissions, useWindowContext } from '../session';
import { Button, ErrorNote, Panel } from '../ui';

/**
 * What a drawer is actually counted in (D-265).
 *
 * Each row is a slot on the count sheet, not a distinct value: **a $1 bill and
 * a $1 coin are both 100 cents** and are counted separately because they are
 * held separately in the till, so the row needs its own key. The API stores
 * `denomination_cents` with no uniqueness on it, and the drawer total is a sum,
 * so two rows at 100 add up correctly.
 *
 * Ordered the way a drawer is counted: notes down from the largest, then coin.
 */
const DENOMINATIONS: { key: string; cents: number; label: string }[] = [
  { key: 'n10000', cents: 10000, label: '$100' },
  { key: 'n5000', cents: 5000, label: '$50' },
  { key: 'n2000', cents: 2000, label: '$20' },
  { key: 'n1000', cents: 1000, label: '$10' },
  { key: 'n500', cents: 500, label: '$5' },
  { key: 'n200', cents: 200, label: '$2' },
  { key: 'n100', cents: 100, label: '$1 bill' },
  { key: 'c100', cents: 100, label: '$1 coin' },
  { key: 'c50', cents: 50, label: '50¢' },
  { key: 'c25', cents: 25, label: '25¢' },
  { key: 'c10', cents: 10, label: '10¢' },
  { key: 'c5', cents: 5, label: '5¢' },
  { key: 'c1', cents: 1, label: '1¢' },
];

/** Count sheet by denomination; the total is what gets recorded. */
function CountSheet({ counts, onChange }: { counts: Record<string, number>; onChange: (next: Record<string, number>) => void }) {
  const total = DENOMINATIONS.reduce((sum, d) => sum + d.cents * (counts[d.key] ?? 0), 0);
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {DENOMINATIONS.map((d) => (
          <label key={d.key} className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-sm">
            <span className="tabular w-16 text-ink-600">{d.label}</span>
            <input type="number" min={0} inputMode="numeric" value={counts[d.key] ?? ''} onChange={(e) => onChange({ ...counts, [d.key]: Math.max(0, Number(e.target.value) || 0) })} className="touch w-full rounded border border-line px-2 text-right" aria-label={`Count of ${d.label}`} />
          </label>
        ))}
      </div>
      <p className="tabular mt-2 text-right text-lg font-semibold">Total {dollars(total)}</p>
    </div>
  );
}

export const toCounts = (counts: Record<string, number>) =>
  DENOMINATIONS.filter((d) => (counts[d.key] ?? 0) > 0).map((d) => ({ denomination_cents: d.cents, quantity: counts[d.key]! }));

/**
 * Swap the hardware under an open register (D-266).
 *
 * A BOCA jams mid-window. Before this, the only way onto the other printer was
 * to close the session -- which means closing and counting the drawer, in front
 * of a queue, because a printer broke. So in practice the window just stopped
 * printing.
 *
 * The list is every printer at the venue, not just this register's, because
 * "both windows onto the one working BOCA" is exactly the situation this is
 * for. Nothing about the money moves: same drawer, same shift, same day.
 */
function HardwarePanel({ context }: { context: WindowContext }) {
  const queryClient = useQueryClient();
  const [hwError, setHwError] = useState<string | null>(null);
  const [hwNotice, setHwNotice] = useState<string | null>(null);
  const session = context.session;
  const change = useMutation({
    mutationFn: (input: { payment_terminal_id?: string | null; ticket_printer_id?: string | null; receipt_printer_id?: string | null }) => endpoints.changeHardware(input),
    onSuccess: () => {
      setHwError(null);
      setHwNotice('Switched. The next ticket prints there.');
      void queryClient.invalidateQueries({ queryKey: ['context'] });
    },
    onError: (cause: ApiError) => {
      setHwNotice(null);
      setHwError(cause.message);
    },
  });
  if (!session) return null;
  const tickets = context.printers.filter((p) => p.printer_model === 'BOCA_FGL');
  const receipts = context.printers.filter((p) => p.printer_model !== 'BOCA_FGL');

  return (
    <Panel title="Hardware" description="Change where this register prints without closing it. The drawer stays open and the shift is untouched.">
      <div className="grid gap-3 p-4 sm:grid-cols-3">
        <label className="block">
          <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Ticket printer</span>
          <select value={session.ticket_printer_id ?? ''} onChange={(e) => change.mutate({ ticket_printer_id: e.target.value || null })} disabled={change.isPending} className="touch mt-1 w-full rounded-lg border border-line px-3">
            <option value="">No ticket printer</option>
            {tickets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.last_status ? ` · ${p.last_status}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Receipt printer</span>
          <select value={session.receipt_printer_id ?? ''} onChange={(e) => change.mutate({ receipt_printer_id: e.target.value || null })} disabled={change.isPending} className="touch mt-1 w-full rounded-lg border border-line px-3">
            <option value="">No receipt printer</option>
            {receipts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.last_status ? ` · ${p.last_status}` : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Card reader</span>
          <select value={session.payment_terminal_id ?? ''} onChange={(e) => change.mutate({ payment_terminal_id: e.target.value || null })} disabled={change.isPending} className="touch mt-1 w-full rounded-lg border border-line px-3">
            <option value="">No card reader</option>
            {context.terminals.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
                {t.simulated ? ' (simulated)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      {hwError ? <ErrorNote>{hwError}</ErrorNote> : null}
      {hwNotice ? <p className="px-4 pb-4 text-sm text-ink-600">{hwNotice}</p> : null}
    </Panel>
  );
}

export default function DrawerPage() {
  const can = usePermissions();
  const askPin = useManagerPin();
  const queryClient = useQueryClient();
  const context = useWindowContext();
  const drawer = useQuery({ queryKey: ['drawer'], queryFn: endpoints.drawer });
  const shifts = useQuery({ queryKey: ['shifts'], queryFn: endpoints.shifts });
  const [openCounts, setOpenCounts] = useState<Record<string, number>>({});
  const [closeCounts, setCloseCounts] = useState<Record<string, number>>({});
  const [movement, setMovement] = useState({ type: 'PAID_OUT', amount: '', reason: '' });
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['drawer'] });
    void queryClient.invalidateQueries({ queryKey: ['shifts'] });
    void queryClient.invalidateQueries({ queryKey: ['context'] });
  };

  const open = useMutation({
    mutationFn: () => endpoints.openDrawer({ counts: toCounts(openCounts) }),
    onSuccess: () => {
      setError(null);
      setNotice('Drawer open.');
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const move = useMutation({
    mutationFn: async () => {
      const cents = Math.round(Number(movement.amount) * 100);
      const attempt = (pin?: string) => endpoints.movement({ movement_type: movement.type, amount_cents: cents, reason: movement.reason, ...(pin ? { manager_pin: pin } : {}) });
      try {
        return await attempt();
      } catch (cause) {
        if ((cause as ApiError).code !== 'MANAGER_APPROVAL_REQUIRED') throw cause;
        const pin = await askPin(`${movement.type.replaceAll('_', ' ').toLowerCase()} of ${dollars(cents)}: ${movement.reason}`);
        if (!pin) throw new Error('Not approved');
        return attempt(pin);
      }
    },
    onSuccess: () => {
      setError(null);
      setNotice('Recorded.');
      setMovement({ type: 'PAID_OUT', amount: '', reason: '' });
      refresh();
    },
    onError: (cause: Error) => setError(cause.message),
  });
  const close = useMutation({
    mutationFn: () => endpoints.closeDrawer({ counts: toCounts(closeCounts) }),
    onSuccess: (result) => {
      setError(null);
      setNotice(`Drawer closed. Expected ${dollars(result.expected.expected_cents)}, counted ${dollars(result.shift.counted_close_cents)}, ${result.shift.over_short_cents === 0 ? 'balanced' : `${(result.shift.over_short_cents ?? 0) < 0 ? 'short' : 'over'} ${dollars(Math.abs(result.shift.over_short_cents ?? 0))}`}.`);
      setCloseCounts({});
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const closeRegister = useMutation({
    mutationFn: (force: boolean) => endpoints.closeSession(context.data!.session!.id, { force, reason: force ? 'Force-closed at the window' : null }),
    onSuccess: () => {
      setNotice('Register closed.');
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const shift = drawer.data?.shift ?? null;
  const expected = drawer.data?.expected ?? null;
  return (
    <div className="mx-auto max-w-4xl space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}

      {/* Above the drawer on purpose: when a printer dies mid-window this is
          what the cashier is looking for, and it must not be below a count
          sheet they have to scroll past. */}
      {context.data ? <HardwarePanel context={context.data} /> : null}

      {!shift ? (
        <Panel title="Open your drawer" description="Count the float you are starting with. Cash sales and refunds from now on belong to this shift.">
          <div className="space-y-4 px-5 py-4">
            <CountSheet counts={openCounts} onChange={setOpenCounts} />
            <Button variant="primary" className="touch text-base" disabled={open.isPending || !can('box_office.drawer.open')} onClick={() => open.mutate()}>
              Open drawer
            </Button>
          </div>
        </Panel>
      ) : (
        <>
          <Panel title="Your drawer" description={`Opened ${when(shift.opened_at)} with a ${dollars(shift.opening_float_cents)} float.`}>
            <div className="grid gap-4 px-5 py-4 md:grid-cols-2">
              <div className="text-sm">
                {expected ? (
                  <dl className="space-y-1">
                    {[
                      ['Opening float', expected.opening_float_cents],
                      [`Cash sales (${expected.cash_sale_count})`, expected.cash_sales_cents],
                      [`Cash refunds (${expected.cash_refund_count})`, -expected.cash_refunds_cents],
                      ['Paid in', expected.paid_in_cents],
                      ['Paid out', -expected.paid_out_cents],
                    ].map(([label, cents]) => (
                      <div key={String(label)} className="flex justify-between">
                        <dt className="text-ink-600">{label}</dt>
                        <dd className="tabular">{dollars(Number(cents))}</dd>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-line pt-1 font-semibold">
                      <dt>Expected in drawer</dt>
                      <dd className="tabular">{dollars(expected.expected_cents)}</dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-ink-400">This register closes blind: count the drawer without seeing the expected total. A manager can see it.</p>
                )}
              </div>
              <form
                className="space-y-2 text-sm"
                onSubmit={(event) => {
                  event.preventDefault();
                  move.mutate();
                }}
              >
                <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Cash in / out</p>
                <select value={movement.type} onChange={(e) => setMovement({ ...movement, type: e.target.value })} className="touch w-full rounded-lg border border-line px-3">
                  <option value="PAID_OUT">Paid out</option>
                  <option value="PAID_IN">Paid in</option>
                  <option value="SAFE_DROP">Safe drop</option>
                  <option value="CHANGE_FUND_IN">Change fund in</option>
                  <option value="CHANGE_FUND_OUT">Change fund out</option>
                </select>
                <input type="number" step="0.01" min="0.01" inputMode="decimal" placeholder="Amount" value={movement.amount} onChange={(e) => setMovement({ ...movement, amount: e.target.value })} className="touch w-full rounded-lg border border-line px-3" aria-label="Amount" />
                <input placeholder="Reason" value={movement.reason} onChange={(e) => setMovement({ ...movement, reason: e.target.value })} className="touch w-full rounded-lg border border-line px-3" aria-label="Reason" />
                <Button type="submit" disabled={move.isPending || !movement.amount || !movement.reason.trim()} className="touch">
                  Record
                </Button>
              </form>
            </div>
          </Panel>
          <Panel title="Close your drawer" description="Count everything in the drawer, including the float. The over/short posts on its own; a manager can correct a miscount afterward.">
            <div className="space-y-4 px-5 py-4">
              <CountSheet counts={closeCounts} onChange={setCloseCounts} />
              <Button variant="danger" className="touch text-base" disabled={close.isPending || !can('box_office.drawer.close') || toCounts(closeCounts).length === 0} onClick={() => close.mutate()}>
                Close drawer
              </Button>
            </div>
          </Panel>
        </>
      )}

      <Panel title="Shifts on this register today">
        <ul className="divide-y divide-line px-5 text-sm">
          {(shifts.data?.shifts ?? []).map((s) => (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                {s.cashier_name} · {when(s.opened_at, undefined, 'time')}
                {s.closed_at ? ` – ${when(s.closed_at, undefined, 'time')}` : ' · open'}
              </span>
              <span className="tabular text-ink-600">
                float {dollars(s.opening_float_cents)}
                {s.status === 'CLOSED' ? ` · counted ${dollars(s.counted_close_cents)} · ${s.over_short_cents === 0 ? 'balanced' : `${(s.over_short_cents ?? 0) < 0 ? 'short' : 'over'} ${dollars(Math.abs(s.over_short_cents ?? 0))}`}` : ''}
              </span>
            </li>
          ))}
          {(shifts.data?.shifts ?? []).length === 0 ? <li className="py-3 text-ink-400">No shifts yet.</li> : null}
        </ul>
      </Panel>

      {context.data?.session ? (
        <Panel title="Close the register" description="After every drawer on it is closed. A manager can force it with drawers still open.">
          <div className="flex gap-2 px-5 py-4">
            <Button disabled={closeRegister.isPending || !can('box_office.session.close')} onClick={() => closeRegister.mutate(false)} className="touch">
              Close register
            </Button>
            {can('box_office.session.force_close') ? (
              <Button variant="danger" disabled={closeRegister.isPending} onClick={() => window.confirm('Force-close with drawers still open?') && closeRegister.mutate(true)} className="touch">
                Force close
              </Button>
            ) : null}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}
